/**
 * VÉRAULT Member Suite — ApprovalBlock (Wave 26.26 · Phase 3d).
 * ─────────────────────────────────────────────────────────────
 * The single client-facing surface for approving (or revising) the
 * itinerary. Lives at the foot of the Itinerary page.
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  YOUR ITINERARY                                       │
 *   │                                                       │
 *   │  We've shaped the days, the rooms, and the reservations  │
 *   │  into one continuous experience. When you're ready, you │
 *   │  can approve — and we'll begin confirming every piece.  │
 *   │                                                       │
 *   │  [ Approve itinerary ]   [ Request revisions ]        │
 *   └──────────────────────────────────────────────────────┘
 *
 * The approve CTA opens a modal containing the canonical legal text;
 * the client must explicitly tick the acknowledgement to enable the
 * final action. Request-revisions opens a calm text-area modal.
 *
 * State variants surfaced inline:
 *   draft / pending           → both CTAs visible
 *   changes_requested         → "Your revisions are with the advisor"
 *                               banner + Approve still available
 *   approved                  → confirmation card with legal-ack stamp
 *                               + read-only comment thread
 *   pending after revision    → "Your revised experience is ready
 *                               for review" gold banner above the CTAs
 *
 * A scroll-collapsing comment thread sits below regardless of state —
 * keeps the two-way conversation alive even after approval.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, MessageSquare, X, AlertCircle, ShieldCheck } from "lucide-react";
import { Heading, EditorialRule, PortalButton, api, fmt } from "../../../../index";

const EASE_CALM = [0.22, 0.61, 0.36, 1];

// ─── Comment thread ────────────────────────────────────────────────
function CommentBubble({ comment }) {
  const isClient = comment.author_role === "client";
  const tone = comment.kind === "revision_request"
    ? "border-[hsl(var(--accent-gold))]/45 bg-[hsl(var(--accent-gold))]/6"
    : isClient
      ? "border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))]"
      : "border-[rgba(99,120,99,0.18)] bg-[rgba(99,120,99,0.05)]";
  return (
    <li
      data-testid={`approval-comment-${comment.id}`}
      data-author-role={comment.author_role}
      data-kind={comment.kind}
      className={`border ${tone} rounded-md px-4 py-3 space-y-1.5`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          {comment.kind === "revision_request" ? "Revision requested · " : ""}
          {comment.author_name}
        </p>
        <p className="font-mono text-[9.5px] tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          {fmt.formatDate(comment.created_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>
      </div>
      <p className="font-accent italic text-[14.5px] text-[hsl(var(--foreground))] whitespace-pre-line leading-relaxed">
        {comment.body}
      </p>
    </li>
  );
}

function CommentThread({ comments, onSubmit, submitting }) {
  const [draft, setDraft] = React.useState("");
  if (!comments || comments.length === 0) {
    return (
      <div className="space-y-3" data-testid="approval-thread-empty">
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Anything you'd like to share with your advisor…"
          className="w-full px-4 py-3 rounded-md border border-[rgba(0,0,0,0.08)] focus:border-[hsl(var(--accent-gold))]/60 focus:outline-none bg-white resize-none font-accent italic text-[14px] leading-relaxed"
          data-testid="approval-comment-input"
        />
        <div className="flex justify-end">
          <PortalButton
            variant="ghost"
            size="sm"
            icon={<MessageSquare className="size-3.5" />}
            onClick={() => { onSubmit(draft); setDraft(""); }}
            disabled={!draft.trim() || submitting}
            data-testid="approval-comment-submit"
          >
            {submitting ? "Sending…" : "Send"}
          </PortalButton>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <ul className="space-y-2.5" data-testid="approval-thread-list">
        {comments.map((c) => <CommentBubble key={c.id} comment={c} />)}
      </ul>
      <div className="space-y-3 pt-2">
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add to the conversation…"
          className="w-full px-4 py-3 rounded-md border border-[rgba(0,0,0,0.08)] focus:border-[hsl(var(--accent-gold))]/60 focus:outline-none bg-white resize-none font-accent italic text-[14px] leading-relaxed"
          data-testid="approval-comment-input"
        />
        <div className="flex justify-end">
          <PortalButton
            variant="ghost"
            size="sm"
            icon={<MessageSquare className="size-3.5" />}
            onClick={() => { onSubmit(draft); setDraft(""); }}
            disabled={!draft.trim() || submitting}
            data-testid="approval-comment-submit"
          >
            {submitting ? "Sending…" : "Send"}
          </PortalButton>
        </div>
      </div>
    </div>
  );
}

// ─── Modals ────────────────────────────────────────────────────────
function ApprovalModal({ open, legalText, busy, onConfirm, onClose }) {
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [comment, setComment] = React.useState("");
  React.useEffect(() => {
    if (!open) { setAcknowledged(false); setComment(""); }
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
      onClick={onClose}
      data-testid="approve-modal-backdrop"
    >
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: EASE_CALM }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[hsl(var(--background))] rounded-md shadow-2xl border border-[rgba(0,0,0,0.06)]"
        data-testid="approve-modal"
      >
        <div className="px-6 py-5 border-b border-[rgba(0,0,0,0.05)] flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Final approval
          </p>
          <button
            type="button"
            onClick={onClose}
            className="size-7 inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] rounded-full transition-colors"
            aria-label="Close"
            data-testid="approve-modal-close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Heading level="h3" className="!text-[24px] sm:!text-[28px]">
            Approve this experience?
          </Heading>
          <div className="border-l-2 border-[hsl(var(--accent-gold))]/60 pl-4 py-1">
            <p className="font-accent italic text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]" data-testid="approve-modal-legal-text">
              {legalText}
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="approve-modal-ack">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="size-4 mt-0.5 accent-[hsl(var(--accent-gold))]"
              data-testid="approve-modal-ack-checkbox"
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-secondary))] group-hover:text-[hsl(var(--foreground))] transition-colors">
              I understand and authorize VÉRAULT Travels to proceed.
            </span>
          </label>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
              Optional note for your advisor
            </p>
            <textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="A small request, a thank-you, anything…"
              className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] focus:border-[hsl(var(--accent-gold))]/60 focus:outline-none bg-white resize-none font-accent italic text-[13.5px]"
              data-testid="approve-modal-comment"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[rgba(0,0,0,0.05)] flex justify-end gap-2">
          <PortalButton variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            icon={<ShieldCheck className="size-3.5" strokeWidth={1.5} />}
            onClick={() => onConfirm({ acknowledged, comment: comment.trim() })}
            disabled={!acknowledged || busy}
            data-testid="approve-modal-confirm"
          >
            {busy ? "Confirming…" : "Approve & proceed"}
          </PortalButton>
        </div>
      </motion.div>
    </div>
  );
}

function ReviseModal({ open, busy, onConfirm, onClose }) {
  const [comment, setComment] = React.useState("");
  React.useEffect(() => { if (!open) setComment(""); }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
      onClick={onClose}
      data-testid="revise-modal-backdrop"
    >
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: EASE_CALM }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[hsl(var(--background))] rounded-md shadow-2xl border border-[rgba(0,0,0,0.06)]"
        data-testid="revise-modal"
      >
        <div className="px-6 py-5 border-b border-[rgba(0,0,0,0.05)] flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Request revisions
          </p>
          <button
            type="button"
            onClick={onClose}
            className="size-7 inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] rounded-full transition-colors"
            aria-label="Close"
            data-testid="revise-modal-close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Heading level="h3" className="!text-[24px] sm:!text-[28px]">
            What would you like to change?
          </Heading>
          <p className="font-accent italic text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
            Be as specific or as open as you like. Your advisor will craft a revised draft and send it back for review.
          </p>
          <textarea
            autoFocus
            rows={5}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Maybe a quieter dinner on Day 3, or an extra night in Antigua…"
            className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] focus:border-[hsl(var(--accent-gold))]/60 focus:outline-none bg-white resize-none font-accent italic text-[14px] leading-relaxed"
            data-testid="revise-modal-comment"
          />
        </div>
        <div className="px-6 py-4 border-t border-[rgba(0,0,0,0.05)] flex justify-end gap-2">
          <PortalButton variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            onClick={() => onConfirm({ comment: comment.trim() })}
            disabled={!comment.trim() || busy}
            data-testid="revise-modal-submit"
          >
            {busy ? "Sending…" : "Send revisions"}
          </PortalButton>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main block ────────────────────────────────────────────────────
export function ApprovalBlock({ tripId }) {
  const [state, setState] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [submittingComment, setSubmittingComment] = React.useState(false);
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [reviseOpen, setReviseOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!tripId) return;
    try {
      const { data } = await api.get(`/client-portal/experiences/${tripId}/approval`);
      setState(data);
      setError(null);
    } catch (e) {
      setError("Couldn't load the approval state.");
    }
  }, [tripId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  if (!state) return null;
  const status = state.trip_status;

  // Submit handlers.
  const submitApprove = async ({ comment }) => {
    setBusy(true);
    try {
      await api.post(`/client-portal/experiences/${tripId}/approve`, {
        acknowledged: true,
        comment: comment || undefined,
      });
      setApproveOpen(false);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't record your approval.");
    } finally { setBusy(false); }
  };
  const submitRevise = async ({ comment }) => {
    setBusy(true);
    try {
      await api.post(`/client-portal/experiences/${tripId}/request-revisions`, { comment });
      setReviseOpen(false);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send the revision request.");
    } finally { setBusy(false); }
  };
  const submitComment = async (body) => {
    if (!body || !body.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/client-portal/experiences/${tripId}/comments`, { body: body.trim() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send the comment.");
    } finally { setSubmittingComment(false); }
  };

  // Status-specific copy + ribbon.
  let ribbon = null;
  let copy = (
    <>
      <Heading level="display" className="!text-[28px] sm:!text-[36px] lg:!text-[44px]">
        Your itinerary.
      </Heading>
      <p className="font-accent italic text-[15px] sm:text-[17px] leading-relaxed text-[hsl(var(--foreground-secondary))] max-w-xl">
        We've shaped the days, the rooms, and the reservations into one continuous experience. When you're ready, approve — we'll begin confirming every piece.
      </p>
    </>
  );

  if (status === "approved") {
    ribbon = (
      <div
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[hsl(168,22%,32%)]/40 bg-[hsl(168,22%,32%)]/8 text-[hsl(168,22%,28%)]"
        data-testid="approval-banner-approved"
      >
        <Check className="size-3.5" strokeWidth={1.8} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          Approved · {state.approval_final_acknowledged_at ? fmt.formatDate(state.approval_final_acknowledged_at, { month: "long", day: "numeric" }) : ""}
        </span>
      </div>
    );
    copy = (
      <>
        <Heading level="display" className="!text-[28px] sm:!text-[36px] lg:!text-[44px]">
          You're set.
        </Heading>
        <p className="font-accent italic text-[15px] sm:text-[17px] leading-relaxed text-[hsl(var(--foreground-secondary))] max-w-xl">
          We've begun confirming every piece. We'll keep this thread open if anything changes.
        </p>
      </>
    );
  } else if (status === "changes_requested") {
    ribbon = (
      <div
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[hsl(var(--accent-gold))]/45 bg-[hsl(var(--accent-gold))]/8 text-[hsl(var(--foreground))]"
        data-testid="approval-banner-revisions-requested"
      >
        <AlertCircle className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          Revisions with your advisor
        </span>
      </div>
    );
  }

  if (state.revised_since_request && status === "pending") {
    ribbon = (
      <div
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[hsl(var(--accent-gold))]/55 bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--foreground))]"
        data-testid="approval-banner-revised-ready"
      >
        <Sparkles className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          Your revised experience is ready for review
        </span>
      </div>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE_CALM }}
      data-testid="approval-block"
      data-trip-status={status}
      data-revised-since-request={state.revised_since_request ? "true" : "false"}
      className="border-t border-[rgba(0,0,0,0.06)] pt-12 sm:pt-16 space-y-7"
    >
      <div className="space-y-4">
        {ribbon}
        {copy}
      </div>

      {error && (
        <div
          className="px-4 py-2.5 rounded-md border border-[#F0C8A8] bg-[#FCEAE0] text-[13px] text-[#8B4513] font-accent italic"
          data-testid="approval-error"
        >
          {error}
        </div>
      )}

      {/* CTAs — visible whenever the client can act. */}
      {(state.can_approve || state.can_request_revisions) && (
        <div className="flex flex-wrap items-center gap-3" data-testid="approval-cta-row">
          {state.can_approve && (
            <PortalButton
              variant="primary"
              size="lg"
              icon={<ShieldCheck className="size-4" strokeWidth={1.5} />}
              onClick={() => setApproveOpen(true)}
              data-testid="approve-itinerary-btn"
            >
              Approve itinerary
            </PortalButton>
          )}
          {state.can_request_revisions && (
            <PortalButton
              variant="ghost"
              size="lg"
              onClick={() => setReviseOpen(true)}
              data-testid="request-revisions-btn"
            >
              Request revisions
            </PortalButton>
          )}
        </div>
      )}

      {/* Two-way comment thread */}
      <div className="pt-3">
        <div className="flex items-baseline gap-3 mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Conversation
          </p>
          <span className="flex-1 h-px bg-[rgba(0,0,0,0.06)]" aria-hidden />
        </div>
        <CommentThread
          comments={state.comments || []}
          onSubmit={submitComment}
          submitting={submittingComment}
        />
      </div>

      <AnimatePresence>
        {approveOpen && (
          <ApprovalModal
            open={approveOpen}
            legalText={state.legal_text}
            busy={busy}
            onConfirm={submitApprove}
            onClose={() => !busy && setApproveOpen(false)}
          />
        )}
        {reviseOpen && (
          <ReviseModal
            open={reviseOpen}
            busy={busy}
            onConfirm={submitRevise}
            onClose={() => !busy && setReviseOpen(false)}
          />
        )}
      </AnimatePresence>
    </motion.section>
  );
}

export default ApprovalBlock;
